import type { HolidayTableSection } from '@/src/types/holidays'

export interface PaymentStep {
    /** The money, e.g. "AED 1,500 per person". */
    amount: string
    /** When it falls due, e.g. "Upon booking confirmation". */
    trigger: string
    /** Anything left over from the source cell, e.g. "Non-refundable deposit." */
    note?: string
}

/** The API puts the trigger in trailing parentheses: "... (Upon booking confirmation)". */
const TRIGGER = /\(([^)]+)\)\s*$/

function toStep(columnLabel: string, cell: string): PaymentStep | null {
    const text = cell.trim()
    const match = TRIGGER.exec(text)
    if (!match) return null

    const body = text.slice(0, match.index).trim()
    const [amount, ...rest] = body.split(/(?<=\.)\s+/)

    return {
        trigger: match[1].trim(),
        amount: (amount || columnLabel).replace(/\.$/, '').trim(),
        note: rest.join(' ').trim() || undefined
    }
}

/**
 * Splits a payment table into dated steps, or `null` when any cell does not name its trigger —
 * a partial schedule would leave a payment unaccounted for, so the caller keeps the plain table.
 */
export function parsePaymentSchedule(section: HolidayTableSection): PaymentStep[] | null {
    const row = section.rows[0]
    if (!row || section.columns.length < 2) return null

    const steps = section.columns
        .map(column => toStep(column.label, row[column.key] ?? ''))
        .filter((step): step is PaymentStep => step !== null)

    return steps.length === section.columns.length ? steps : null
}
